import { useNavigate, useParams } from "react-router-dom";

import { Button, Textarea } from "components";
import ChatMessage from "components/ChatMessage";
import { HeaderWithBack } from "components/HeaderBack";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createMessage, getMessages } from "services/message";
import { RootState } from "store";
import * as Yup from "yup";
import styles from "./styles.module.scss";

type Message = {
	id: string;
	message: string;
	message_source: number;
	message_from: string;
	created_at: string;
};

const MessageSchema = Yup.object().shape({
	message: Yup.string().required("Campo obrigatório")
});

type MessageFormValues = {
	id: string;
	message: string;
};

export default function MessagesPage() {
	const navigate = useNavigate();

	const endRef = useRef<HTMLDivElement>(null);

	const { logged_user } = useSelector((state: RootState) => state.tokens);

	const { tripId } = useParams();
	const [messages, setMessages] = useState<Message[]>([]);
	const [submitting, setSubmitting] = useState(false);

	const fetchData = async () => {
		if (!logged_user) return;

		const allMessages = (await getMessages(tripId)) ?? [];

		allMessages.sort((a: Message, b: Message) => {
			const dateA = new Date(a.created_at).getTime();
			const dateB = new Date(b.created_at).getTime();
			return dateA - dateB;
		});

		setMessages(allMessages);
	};

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
	}, [messages]);

	const formik = useFormik<MessageFormValues>({
		initialValues: {
			id: "",
			message: ""
		},
		validationSchema: MessageSchema,
		onSubmit: async (form) => {
			if (!tripId) return;

			setSubmitting(true);

			await createMessage(tripId, form)
				.then(() => {
					setSubmitting(false);
					formik.resetForm();
					fetchData();
				})
				.catch((error) => {
					setSubmitting(false);
					let message = "Algo saiu errado. ";

					if (error.code < 500) {
						message += "Verifique os dados digitados e tente novamente.";
						if (error.body.message) formik.errors.message = error.body.message;
					} else {
						message += "Entre em contato com o suporte.";
					}

					toast.error(message, {
						position: "bottom-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						progress: undefined,
						theme: "light"
					});
				});
		},
		enableReinitialize: true
	});

	return (
		<div className={styles.container}>
			<HeaderWithBack
				label="Mensagens"
				backTo={-1}
				className={styles.messagesHeader}
			/>
			<div className={styles.messageListing}>
				{messages.map((message) => (
					<ChatMessage
						key={message.id}
						messageSource={message.message_source}
						message={message.message}
						messageSender={message.message_from}
						timestamp={message.created_at}
					/>
				))}
				<div ref={endRef} />
			</div>
			<form onSubmit={formik.handleSubmit}>
				<Textarea
					id="message"
					label=""
					name="message"
					placeholder="Escreva sua mensagem"
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					value={formik.values.message}
					error={formik.touched.message && formik.errors.message}
					errorMessage={formik.errors.message}
				/>
				<Button type="submit" disabled={submitting}>
					Enviar
				</Button>
			</form>
		</div>
	);
}
